import React, { FC, memo, useCallback, useMemo } from "react";

import { FiltersContainerWrapper } from "./styled";
import { ContainerLoading } from "../components/Loading/ContainerLoading";
import { ExpandableTitle } from "../components/ExpandableTitle/ExpandableTitle";
import { HiddenTitleItems } from "./HiddenTitleItems";
import { isVisibleContainer } from "../utils/isVisibleContainer";
import { getFilterSelectedItems } from "../utils/getFilterSelectedItems";
import { checkIsLoading } from "../utils/checkIsLoading";
import { getFilterComponent } from "../utils/getFilterComponent";
import { useWidgetContext } from "../../Dashboard/hooks/useWidgetContext";
import { ConfigContainerChild, ContainerProps, FilterType } from "../types";

export const FiltersContainer: FC<ContainerProps> = memo(({ elementConfig, config, type, renderElement }) => {
  const { filters, dataSources, expandedContainers } = useWidgetContext(type);

  const { filters: configFilters } = config || {};
  const { id, style, options } = elementConfig || {};
  const { padding, bgColor, fontColor, fontSize, expandable, expanded } = options || {};

  const isLoading = useMemo(
    () => checkIsLoading(dataSources, config, configFilters),
    [configFilters, config, dataSources],
  );

  const filterItems = useMemo(
    () => elementConfig?.children?.filter(child => child.options?.filterName),
    [elementConfig?.children],
  );

  const renderFilter = useCallback(
    (filter: ConfigContainerChild, index: number) => {
      const FilterComponent = getFilterComponent(filter.type as FilterType);

      return <FilterComponent key={index} type={type} filter={filter} config={config} elementConfig={elementConfig} />;
    },
    [config, elementConfig, type],
  );

  const isVisible = isVisibleContainer(id, expandable, expanded, expandedContainers);

  const selectedItems = useMemo(
    () => getFilterSelectedItems(filterItems, filters, configFilters),
    [configFilters, filters, filterItems],
  );

  return (
    <>
      <div style={{ marginBottom: !isVisible && selectedItems.length ? "2rem" : 0 }}>
        <ExpandableTitle elementConfig={elementConfig} type={type} renderElement={renderElement} />
      </div>
      {isLoading && <ContainerLoading />}
      {!isLoading && isVisible && (
        <FiltersContainerWrapper
          style={style}
          $padding={padding}
          $bgColor={bgColor}
          $fontSize={fontSize}
          $fontColor={fontColor}
        >
          {filterItems?.map(renderFilter)}
        </FiltersContainerWrapper>
      )}
      <HiddenTitleItems
        elementConfig={elementConfig}
        config={config}
        type={type}
        filter={filterItems[0].options?.filterName}
      />
    </>
  );
});
